import { ComponentNoData } from 'components/ComponentLoader';
import { ComponentError } from 'components/ComponentLoader';
import CustomButton, { OutlinedButton, DangerButton } from 'components/CustomButton';
import DropdownMenu, {
  DropdownItem,
  DropdownItemGroup
} from '@atlaskit/dropdown-menu';
import CustomIcon from 'components/CustomIcon';
import { CommonTab } from 'components/tab/CustomTab';
import { CustomTabRef } from 'components/tab/CustomTab';
import { CommonTabGroup } from 'components/tab/TabGroup';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchPages } from 'redux/slices/bannerSlices';
import { setSelectedPage } from 'redux/slices/bannerSlices';
import { fetchBanners } from 'redux/slices/bannerSlices';
import ConfirmModal from 'components/ConfirmModal';
import { tryDeleteBannerType } from 'redux/slices/bannerSlices';
import BannerTypeCreatorModal from "./components/BannerTypeCreatorModal";
import { useLocation } from 'react-router';

const BannerNavBar = ({ noItemsInTab = 5 }) => {
  const {
    pages: { init, loading, error, data, selectedPage, processing },
    banners: { activeTab }
  } = useSelector((state) => state.banner);

  const [tabItems, setTabItems] = useState(data.slice(0, noItemsInTab));
  const [dropDownItems, setDropDownItems] = useState(
    data.slice(noItemsInTab, data.length)
  );

  const [showBannerTypeForm, setShowBannerTypeForm] = useState();

  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const activePage = data.find((d) => d.id === selectedPage);

  const dispatch = useDispatch();

  const location = useLocation()

  useEffect(() => {
    if (!init && !loading && !error) {
      dispatch(fetchPages());
    }
  }, [dispatch, init, loading, error]);

  useEffect(() => {
    if (data.length) {
      const urlParam = new URLSearchParams(location.search)
      const id = urlParam.get('id')
      const activePage = data.find((x) => x.id === id)
      dispatch(setSelectedPage({ page: activePage?.id ?? data[0].id }));
      fetchBannerListAction(true, activePage ?? data[0], activeTab, 1);
    }
    setShowBannerTypeForm(undefined);
    setShowConfirmModal(false);
  }, [data]);

  const fetchBannerListAction = (
    needApiCall,
    screen = activePage,
    tab = activeTab,
    page = 1
  ) => {
    dispatch(
      fetchBanners({
        apiCall: needApiCall,
        screen: screen?.value,
        tab: tab?.value,
        page
      })
    );
    // navigate(`${currentPath}?screen=${screen.value}&tab=${tab.value}&page=${page}`, {replace: true})
  };

  const hasNoData = init && !data.length;

  useEffect(() => {
    if (!init) return;
    if (
      tabItems.length &&
      data.length > noItemsInTab &&
      !tabItems.some((t) => t.id === selectedPage)
    ) {
      const tab = data.find((d) => d.id === selectedPage);
      const removedItem = tabItems[tabItems.length - 1];
      setTabItems([...tabItems.slice(0, tabItems.length - 1), tab]);
      setDropDownItems(
        dropDownItems.map((item) =>
          item.title === tab.title ? removedItem : item
        )
      );
    } else {
      setTabItems(data.slice(0, noItemsInTab));
      setDropDownItems(data.slice(noItemsInTab, data.length));
    }
  }, [data]);

  const onSelect = (tab, fromDropDown = false) => {
    if (tab.id !== selectedPage) {
      dispatch(setSelectedPage({ page: tab.id }));
      fetchBannerListAction(true, tab, activeTab);

      const url = new URL(window.location)
      url.searchParams.set('id', tab.id)
      window.history.replaceState({}, '', url)

      if (fromDropDown) {
        const removedItem = tabItems[tabItems.length - 1];

        setTabItems([...tabItems.slice(0, tabItems.length - 1), tab]);
        setDropDownItems(
          dropDownItems.map((item) =>
            item.title === tab.title ? removedItem : item
          )
        );
      }
    }
  };

  const removeBannerPageType = () => {
    dispatch(tryDeleteBannerType(selectedPage));
  };

  return (
    <>
      {error ? (
        <ComponentError
          errorMsg={error}
          retry={() => dispatch(fetchPages())}
        />
      ) : hasNoData ? (
        <ComponentNoData />
      ) : (
        <CommonTabGroup aside=
          {init && (
            <div className="flex items-start shrink-0 space-x-2">
              <OutlinedButton
                onClick={() => setShowBannerTypeForm({})}
                isIconButton={true}
                title="Add Banner Type"
              >
                <CustomIcon
                  icon="add"
                  size="lg"
                />
              </OutlinedButton>
              <CustomButton
                onClick={() => setShowBannerTypeForm(activePage)}
                isIconButton={true}
                title="Edit Banner Type"
              >
                <CustomIcon
                  icon="edit"
                  size="lg"
                />
              </CustomButton>
              <DangerButton
                onClick={() => setShowConfirmModal(true)}
                disabled={!activePage?.deletable}
                isIconButton={true}
                title="Delete Banner Type"
              >
                <CustomIcon
                  icon="delete"
                  size="lg"
                />
              </DangerButton>
            </div>
          )}
        >
          {(!init || loading) && (
            <>
              {Array.from({ length: 4 }, (_, i) => (
                <CommonTab
                  key={i}
                  className={i == [0] ? "border-rand-300" : "border-transparent"}
                  disabled={true}
                >
                  <div className="bg-rand-400 w-9 h-5" />
                </CommonTab>
              ))}
            </>
          )}

          {tabItems.map((tab, i) => (
            <CommonTab
              key={`page${tab.id}`}
              onClick={() => onSelect(tab)}
              disabled={loading}
              active={tab.id === selectedPage}
            >
              {tab.title}
            </CommonTab>
          ))}

          {!!dropDownItems.length && (
            <DropdownMenu
              placement="bottom-end"
              shouldFlip
              isLoading={loading}
              trigger={({ triggerRef, isSelected, testId, ...props }) => (
                <CustomTabRef
                  ref={triggerRef}
                  {...props}
                  active={isSelected}
                  className="flex items-center px-3"
                >
                  <CustomIcon
                    icon="more_horiz"
                    size="xl"
                    className="cursor-pointer leading-7"
                  />
                </CustomTabRef>
              )}
            >
              <DropdownItemGroup>
                {dropDownItems.map((item, i) => (
                  <DropdownItem
                    key={`${item.id}`}
                    onClick={() => onSelect(item, true)}
                  >
                    {item.title}
                  </DropdownItem>
                ))}
              </DropdownItemGroup>
            </DropdownMenu>
          )}
        </CommonTabGroup>
      )}

      {!!showBannerTypeForm && <BannerTypeCreatorModal setShowModal={setShowBannerTypeForm} showModal={showBannerTypeForm} />}

      <ConfirmModal
        children="This will remove banner type. Are you sure?"
        disabled={false}
        showModal={showConfirmModal}
        setShowModal={() => setShowConfirmModal()}
        onConfirm={removeBannerPageType}
      />
    </>
  );
};

export default BannerNavBar;
